import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getBranches } from '@/api/lookups.api';
import { transferStudentBranch } from '@/api/students.api';
import { ApiError } from '@/api/errors';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { FormField } from '@/components/form/FormField';
import { LookupSelect } from '@/components/form/LookupSelect';
import { showSuccessToast } from '@/lib/toast';
import { lookupLabel } from '@/lib/lookups';
import type { LookupItem, StudentResponse } from '@/types/shared';

interface TransferBranchPanelProps {
  student: StudentResponse;
  onTransferred: (updated: StudentResponse) => void;
}

/**
 * T175 (§20, DEC-BR17/FR-20–24, `docs/design/19-branch-scoping-ux.md` §4) — Admin-only student
 * branch transfer. Two steps, deliberately not collapsed into one screen (§4.2): step 1 (this
 * component's own inline card, reusing `.fa-form-section`/`.fa-form-actions` — no new CSS
 * primitive) picks the destination; step 2 is the actual `ConfirmDialog`, whose copy is T159's
 * exact FR-23 wording, verbatim in both languages. Options for step 1 are every ACTIVE branch
 * except the student's current one — the same "fetch and render whatever the already-scoped
 * response contains" pattern as every other branch picker (Admin is always all-branches, so no
 * further client-side scope narrowing applies here, unlike the registration picker in
 * `StudentFormPage`).
 *
 * The critical, easy-to-get-wrong detail (per T156 §5.3 / DEC-BR17): the transfer moves the
 * student's ONGOING record only. It never rewrites already-recognized revenue, so the step-2 copy
 * must say so plainly — `confirmBodyStays` below is rendered in `<strong>` specifically because
 * FR-23's own rationale is that a skimming reader is the most likely to miss the negation.
 */
export function TransferBranchPanel({ student, onTransferred }: TransferBranchPanelProps) {
  const { t, i18n } = useTranslation();

  const [panelOpen, setPanelOpen] = useState(false);
  const [branches, setBranches] = useState<LookupItem[]>([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [targetBranchId, setTargetBranchId] = useState<number | ''>('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [panelError, setPanelError] = useState<string | null>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [transferring, setTransferring] = useState(false);

  function openPanel() {
    setPanelOpen(true);
    setFieldError(null);
    setPanelError(null);
    setTargetBranchId('');
    if (branches.length === 0 && !loadingBranches) {
      setLoadingBranches(true);
      setLoadError(null);
      getBranches()
        .then(setBranches)
        .catch(() => setLoadError(t('students.transfer.loadBranchesError')))
        .finally(() => setLoadingBranches(false));
    }
  }

  function closePanel() {
    setPanelOpen(false);
    setFieldError(null);
    setPanelError(null);
    setTargetBranchId('');
  }

  // Every active branch except the student's current one (§4.2 step 1: "Cairo Branch excluded
  // from the options — you can't transfer to the same branch").
  const selectableBranches = branches.filter((branch) => branch.id !== student.branch.id);

  function handleContinue() {
    setFieldError(null);
    if (targetBranchId === '') {
      setFieldError(t('students.transfer.newBranchRequired'));
      return;
    }
    setPanelOpen(false);
    setConfirmOpen(true);
  }

  async function handleConfirmTransfer() {
    if (targetBranchId === '') return;
    setTransferring(true);
    try {
      const updated = await transferStudentBranch(student.studentCode, {
        toBranchId: targetBranchId,
      });
      setConfirmOpen(false);
      setTargetBranchId('');
      showSuccessToast(
        t('students.transfer.successToast', {
          newBranch: lookupLabel(updated.branch, i18n.language),
        }),
      );
      onTransferred(updated);
    } catch (err) {
      // §4.4 "Alternate — validation failure": inline error inside the step-1 panel, no toast, and
      // the confirm dialog never reopens a second time until the Admin corrects the selection.
      const apiError = ApiError.fromUnknown(err);
      setConfirmOpen(false);
      setPanelOpen(true);
      setPanelError(apiError.message || t('students.transfer.genericError'));
    } finally {
      setTransferring(false);
    }
  }

  const currentBranchLabel = lookupLabel(student.branch, i18n.language);
  const selectedBranchLabel =
    targetBranchId === ''
      ? ''
      : lookupLabel(
          branches.find((branch) => branch.id === targetBranchId),
          i18n.language,
        );

  return (
    <>
      <button type="button" className="fa-btn fa-btn--ghost" onClick={openPanel}>
        {t('students.transfer.actionButton')}
      </button>

      {panelOpen && (
        <section className="fa-form-section">
          <h2 className="fa-form-section__title">{t('students.transfer.step1Title')}</h2>
          <p className="fa-form-section__description">
            {t('students.transfer.currentBranchLabel', { branch: currentBranchLabel })}
          </p>

          {panelError && (
            <p className="fa-field__error" role="alert">
              {panelError}
            </p>
          )}
          {loadError && (
            <p className="fa-field__error" role="alert">
              {loadError}
            </p>
          )}

          <FormField
            id="transferBranchId"
            label={t('students.transfer.newBranchLabel')}
            required
            error={fieldError ?? undefined}
            hint={t('students.transfer.sameBranchHint', { branch: currentBranchLabel })}
          >
            <LookupSelect
              id="transferBranchId"
              value={targetBranchId}
              onChange={setTargetBranchId}
              items={selectableBranches}
              placeholder={t('students.transfer.newBranchPlaceholder')}
              disabled={loadingBranches || transferring}
            />
          </FormField>

          <div className="fa-form-actions">
            <button
              type="button"
              className="fa-btn fa-btn--secondary"
              disabled={transferring}
              onClick={closePanel}
            >
              {t('students.form.cancel')}
            </button>
            <button
              type="button"
              className="fa-btn fa-btn--primary"
              disabled={loadingBranches || transferring}
              onClick={handleContinue}
            >
              {t('students.transfer.continueButton')}
            </button>
          </div>
        </section>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title={t('students.transfer.confirmTitle', { newBranch: selectedBranchLabel })}
        description={
          <>
            <p>{t('students.transfer.confirmBodyMoves', { newBranch: selectedBranchLabel })}</p>
            <p>
              <strong>
                {t('students.transfer.confirmBodyStays', { oldBranch: currentBranchLabel })}
              </strong>
            </p>
          </>
        }
        confirmLabel={t('students.transfer.confirmButton')}
        cancelLabel={t('students.form.cancel')}
        confirmDisabled={transferring}
        onConfirm={handleConfirmTransfer}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}
